import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Iter XX.46 — Milestone celebration overlay.
 * Frontend-only. Compares the current achievements list against the
 * ones the user has already seen (stored in localStorage) and pops a
 * small emerald toast for anything new.
 *
 * Zero backend calls. The achievements list is already fetched by
 * the parent dashboard.
 */
export default function MilestoneCelebration({ achievements }) {
  const [showing, setShowing] = useState(null);

  useEffect(() => {
    if (!Array.isArray(achievements) || achievements.length === 0) return;
    let seen = new Set();
    try { seen = new Set(JSON.parse(localStorage.getItem("gully_seen_milestones") || "[]")); } catch { /* ignore */ }
    const unseen = achievements.find(a => a && a.code && !seen.has(a.code));
    if (!unseen) return;
    setShowing(unseen);
    seen.add(unseen.code);
    try { localStorage.setItem("gully_seen_milestones", JSON.stringify(Array.from(seen))); } catch { /* ignore */ }
    // Auto-dismiss after 8 seconds.
    const t = setTimeout(() => setShowing(null), 8000);
    return () => clearTimeout(t);
  }, [achievements]);

  if (!showing) return null;

  return (
    <div
      className="fixed bottom-6 right-6 max-w-sm bg-gradient-to-br from-emerald-500 to-emerald-700 text-neutral-950 rounded-2xl shadow-2xl p-4 z-50 border border-emerald-400"
      data-testid={`milestone-toast-${showing.code}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest opacity-70 mb-1">Milestone unlocked</div>
          <div className="text-base font-semibold" data-testid="milestone-toast-title">🎉 {showing.title}</div>
          {showing.body && <div className="text-sm mt-1 opacity-90">{showing.body}</div>}
        </div>
        <button
          onClick={() => setShowing(null)}
          className="p-1 rounded hover:bg-neutral-950/10"
          aria-label="Dismiss"
          data-testid="milestone-toast-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
